<template>
  <div class="bg-white p-5 space-y-4">
    <template v-for="question in section.questions" :key="question.id">
      <div v-if="question.summary" class="relative">
        <!-- Show an answere|d card only if there's a non-empty answer (including non-empty arrays) -->
        <AnsweredCard
          v-if="hasAnswer(question.id)"
          :question="question"
          :answer="retrieveAnswer(question.id)"
          :badgeClass="booleanBadgeClass(question.id)"
          :label="getBooleanLabel(question.id)"
          :list="listAnswerItems(question.id)"
          :colors="colorValues(question.id)"
          :palette="renderPaletteSummary(question.id)"
          @fill="emitNavigate(sectionIndex)" />

        <!-- Otherwise prompt to fill in the answer -->
        <EmptyPrompt v-else :label="question.summary.label" @fill="emitNavigate(sectionIndex)" />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { useBlueprintSummary } from "@/features/blueprint/composables/useBlueprintSummary";
import { renderPalettePickerSummary } from "@/composables/useColorPalettes";
import { QuestionSchema } from "@/features/blueprint/schema/questions/QuestionSchema";
import { Answers } from "@/features/blueprint/schema/answers/Answers";
import AnsweredCard from "@/features/blueprint/components/summary/AnsweredCard.vue";
import EmptyPrompt from "@/features/blueprint/components/summary/EmptyPrompt.vue";

const props = defineProps<{ section: QuestionSchema; answers: Answers; sectionIndex: number }>();

const emit = defineEmits<{ (e: "navigate", index: number): void }>();
function emitNavigate(sectionIndex: number) {
  emit("navigate", sectionIndex);
}
const blueprint = useBlueprintSummary(props.answers);
const { retrieveAnswer, isNonEmptyAnswer, booleanBadgeClass, getBooleanLabel, listAnswerItems, colorValues } =
  blueprint;

/**
 * Unified check for non-empty answers, including arrays with length > 0
 */
function hasAnswer(id: string): boolean {
  const ans = retrieveAnswer(id);
  if (Array.isArray(ans)) {
    return ans.length > 0;
  }
  return isNonEmptyAnswer(ans);
}

function renderPaletteSummary(id: string) {
  const val = retrieveAnswer(id);
  return typeof val === "string" ? renderPalettePickerSummary(val) : null;
}
</script>
